import type { ChangeEvent } from "react";
import { useMemo, useRef, useState } from "react";
import { Container, Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";
import Card from "../../../../components/Card/Card";
import { useGetAllBusinessCardsQuery } from "../../../../rq/hooks/useQueries";
import {
  CardsWrapper,
  Description,
  LogoCarouselWrapper,
  LogoImage,
  LogoItem,
  LogoSection,
  LogoTrack,
  MainWrapper,
  Title,
} from "../styles";

import { LogoAsset } from "../types";

type CompaniesCardsProps = {
  logos?: LogoAsset[];
};

export default function CompaniesCards({ logos }: CompaniesCardsProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const safeLogos = useMemo<LogoAsset[]>(() => logos ?? [], [logos]);

  const { data, refetch } = useGetAllBusinessCardsQuery(page, null, null, {
    keepPreviousData: true,
  });
  const cardLogos = useMemo<LogoAsset[]>(() => {
    if (!data?.data) {
      return [];
    }

    const uniqueLogos = new Map<string, string>();

    data.data.forEach((card: any) => {
      const src = card.profilePicture || card.businessLogoPath;

      if (!src || uniqueLogos.has(src)) {
        return;
      }

      const label =
        card.businessName || card.name || t("home.companies_cards.logo_alt");

      uniqueLogos.set(src, label);
    });

    return Array.from(uniqueLogos.entries()).map(([src, label]) => ({
      src,
      label,
    }));
  }, [data?.data, t]);

  const displayLogos = useMemo<LogoAsset[]>(() => {
    if (safeLogos.length === 0 && cardLogos.length === 0) {
      return [];
    }

    const merged = new Map<string, string>();

    safeLogos.forEach(({ src, label }) => {
      if (src && !merged.has(src)) {
        merged.set(src, label);
      }
    });

    cardLogos.forEach(({ src, label }) => {
      if (src && !merged.has(src)) {
        merged.set(src, label);
      }
    });

    return Array.from(merged.entries()).map(([src, label]) => ({
      src,
      label,
    }));
  }, [cardLogos, safeLogos]);

  const hasLogos = displayLogos.length > 0;
  const shouldAnimate = displayLogos.length > 1;

  const duplicatedLogos = useMemo(
    () =>
      displayLogos.length > 1
        ? [...displayLogos, ...displayLogos]
        : displayLogos,
    [displayLogos],
  );

  const animationDuration = useMemo(
    () => Math.max(100, displayLogos.length * 3),
    [displayLogos.length],
  );

  const handleChange = (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Container>
      <MainWrapper>
        <Title>{t("home.companies_cards.title")}</Title>
        <Description>{t("home.companies_cards.description")}</Description>
        <CardsWrapper>
          {data?.data?.map((card: any, i: any) => (
            <Card
              key={i}
              profilePicture={card.profilePicture}
              candidateUserId={card.candidateUserId}
              candidateId={card.candidateId}
              professions={card.profession}
              businessUserId={card.businessUserId}
              businessId={card.businessId}
              name={card.name}
              lastName={card.lastName}
              country={card.country}
              video={card.video}
              videoViews={card.videoViews}
              likes={card.likes}
              saves={card.saves}
              isMyFavorite={card.isMyFavorite}
              queryKeyString="get-all-business-cards"
              likedFromMe={card.likedFromMe}
              pageNumber={data.metaData.pageNumber}
              refetch={refetch}
              videoId={card.videoId}
              boughtFromCurrentUser={card.boughtFromCurrentUser}
              style={{ border: "1px solid #D0D0D0", borderRadius: "20px" }}
            />
          ))}
        </CardsWrapper>
        <Pagination
          count={data?.metaData?.pageCount}
          onChange={handleChange}
          variant="outlined"
          shape="rounded"
          hidePrevButton
          hideNextButton
          sx={{ alignSelf: "center" }}
        />
      </MainWrapper>
      {hasLogos ? (
        <LogoSection>
          <Title>{t("home.companies_cards.trust_title")}</Title>
          <LogoCarouselWrapper ref={wrapperRef} $isScrollable={shouldAnimate}>
            <LogoTrack
              ref={trackRef}
              $isScrollable={shouldAnimate}
              $duration={animationDuration}
            >
              {duplicatedLogos.map((logo, index) => (
                <LogoItem key={`${logo.src}-${index}`}>
                  <LogoImage
                    src={logo.src}
                    alt={logo.label}
                    draggable={false}
                  />
                </LogoItem>
              ))}
            </LogoTrack>
          </LogoCarouselWrapper>
        </LogoSection>
      ) : null}
    </Container>
  );
}
